import React, { useMemo, useState, forwardRef } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldCustom, FieldMarkdown, FieldImageGalleryUpload, FieldToggle
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'
import { linkRegex } from 'services/constants'

const ItemSchema = Yup.object().shape({
  text: Yup.string().required('Campo obrigatório'),
  description: Yup.string().required('Campo obrigatório'),
  image: Yup.object({
    url: Yup.string().required('Campo de imagem obrigatório'),
    alt: Yup.string(),
    title: Yup.string()
  }).required('Campo de imagem obrigatório'),
  useLink: Yup.boolean(),
  href: Yup.lazy(value => (Yup.string()
    .matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .when('useLink', {
      is: (value) => (value && value === true),
      then: () => (Yup.string().required('Campo obrigatório')),
      otherwise: () => (Yup.string().nullable(true))
    })))
});

// eslint-disable-next-line react/prop-types
const CampusForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(() => {
    const {
      text, description, backgroundImage, alt, href
    } = initialValues
    return {
      text,
      description,
      image: {
        url: backgroundImage,
        alt,
        title: '',
        isLocal: false,
      },
      useLink: href !== null,
      href
    }
  }, [initialValues])

  const formToModuleSchema = (values) => {
    const {
      text, description, image: { url, alt }, href, useLink
    } = values

    return {
      text,
      description,
      backgroundImage: url,
      alt,
      backgroundColor: '#fff',
      textColor: '#000',
      hasText: true,
      href: useLink ? href : null
    }
  }

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(formToModuleSchema(values));
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      {({ values }) => (
        <Form>
          <Field
            title="Imagem"
            name="image"
            component={FieldImageGalleryUpload}
          />
          <Field
            title="Título"
            name="text"
            info="Título do destaque"
            component={FieldCustom}
          />
          <Field
            title="Descrição"
            name="description"
            info="Texto complementar"
            component={FieldMarkdown}
          />
          <Field
            title="Com Link"
            name="useLink"
            info="Destaque com link"
            component={FieldToggle}
          />
          {
            values.useLink !== false && (
              <Field
                title="Link"
                name="href"
                info="Link ao clicar no titulo "
                component={FieldCustom}
              />
            )
          }
        </Form>
      )}
    </Formik>
  )
})

const Schema = Yup.object().shape({
  data: Yup.array().of(Yup.object().shape({
    text: Yup.string().required('Campo obrigatório'),
    description: Yup.string().required('Campo obrigatório'),
    backgroundImage: Yup.string().required('Campo obrigatório'),
    alt: Yup.string(),
  }))
    .required('Campo obrigatório'),
});

const CampusEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)

  const initialValues = useMemo(() => {
    const { data } = initialProps;
    return { data };
  }, [initialProps]);

  const handleFormChange = newValues => {
    const newProps = { ...initialProps, ...newValues };
    const isValid = Schema.isValidSync(newProps)
    if (isValid) setEditorProps(newProps)
  };

  useDebounce(() => {
    onChange(editorProps);
  }, 200, [editorProps])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      validate={handleFormChange}
    >
      <Form>
        <Field
          name="data"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'text',
              Header: 'Título',
            },
            {
              accessor: 'description',
              Header: 'Descrição',
            }
          ]}
          rowKey="text"
          ItemForm={CampusForm}
        />
      </Form>
    </Formik>
  );
};
CampusEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      description: PropTypes.string,
    }))
  }),
};

CampusEditor.defaultProps = {
  initialProps: {},
};

export default CampusEditor;
